import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { RelayLoader } from "../components/RelayLoader";

const API = process.env.REACT_APP_BACKEND_URL;
const INK = "#2A1E5C";
const GOLD = "#F5CB5C";

const CONFIGS = {
  free_sprint: {
    productId: "free_sprint",
    headline: "Run Four Relays Free",
    copy: "Your 28-day Free Sprint: map your real weekly number, get a 4-week race sheet built in your own Google Drive, and run the week from Payday Relay. Four full relays to find your rhythm.",
    fallbackCta: "Start Free Sprint · $0 today",
  },
  season_monthly: {
    productId: "season_monthly",
    headline: "Start Your Season",
    copy: "The full 52-week Payday Relay race: every weekly relay, the Bonus Bank, PTO Bank, Quarterly Tax Days, your race scoreboard, and full Google Sheet sync. If you ran a Free Sprint, your race starts fresh, keep your Sprint settings or adjust your first quarter before you begin.",
    fallbackCta: "Start My Season · $19/mo",
    note: "$19/mo · cancel anytime",
  },
  season_annual: {
    productId: "season_annual",
    headline: "Start Your Season",
    copy: "The full 52-week Payday Relay race: every weekly relay, the Bonus Bank, PTO Bank, Quarterly Tax Days, your race scoreboard, and full Google Sheet sync. If you ran a Free Sprint, your race starts fresh, keep your Sprint settings or adjust your first quarter before you begin.",
    fallbackCta: "Start My Season · $119/yr",
    note: "$119/yr, save 48% vs monthly",
  },
};

function EmbeddedCheckout({ cfg, betaSignup, sprintPlan }) {
  const mountRef = useRef(null);
  const checkoutRef = useRef(null);
  const [state, setState] = useState("loading"); // loading | embedded | fallback
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState("loading");
    (async () => {
      try {
        const res = await fetch(`${API}/api/payments/embedded-checkout`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            product_id: cfg.productId,
            return_url: `${window.location.origin}/checkout/return`,
            source_page: window.location.pathname + window.location.search,
            beta_signup: !!betaSignup,
            sprint_plan: sprintPlan || "monthly",
          }),
        });
        const data = await res.json();
        if (!res.ok || !data.client_secret || !data.publishable_key) throw new Error("embedded unavailable");
        if (!window.Stripe) {
          let s = document.querySelector('script[src="https://js.stripe.com/v3/"]');
          if (!s) {
            s = document.createElement("script");
            s.src = "https://js.stripe.com/v3/";
            document.head.appendChild(s);
          }
          await new Promise((resolve, reject) => {
            if (window.Stripe) return resolve();
            s.addEventListener("load", resolve);
            s.addEventListener("error", reject);
          });
        }
        if (cancelled) return;
        const stripe = window.Stripe(data.publishable_key);
        const checkout = await stripe.initEmbeddedCheckout({ clientSecret: data.client_secret });
        if (cancelled) { checkout.destroy(); return; }
        checkoutRef.current = checkout;
        checkout.mount(mountRef.current);
        setState("embedded");
      } catch {
        if (!cancelled) setState("fallback");
      }
    })();
    return () => {
      cancelled = true;
      if (checkoutRef.current) { checkoutRef.current.destroy(); checkoutRef.current = null; }
    };
  }, [cfg.productId, betaSignup, sprintPlan]);

  const hostedCheckout = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ product_id: cfg.productId, origin_url: `${window.location.origin}/checkout/return?product=${cfg.productId}` }),
      });
      const d = await res.json();
      const url = d.url || d.checkout_url;
      if (url) window.location.href = url;
      else throw new Error();
    } catch {
      toast.error("Checkout is briefly unavailable, try again in a minute.");
      setBusy(false);
    }
  };

  return (
    <div>
      {state === "loading" && (
        <div className="w-full rounded-2xl py-16 text-center shadow-2xl" style={{ background: "#FFFFFF" }} data-testid="start-checkout-loading">
          <RelayLoader label="Loading secure checkout…" />
        </div>
      )}
      {/* Stripe Embedded Checkout mounts here */}
      <div
        id="checkout"
        ref={mountRef}
        className={state === "embedded" ? "w-full rounded-2xl overflow-hidden shadow-2xl p-4 sm:p-5 pb-8 sm:pb-9" : ""}
        style={state === "embedded" ? { background: GOLD } : undefined}
        data-testid="embedded-checkout-mount"
      />
      {state === "fallback" && (
        <div className="text-center" data-testid="start-checkout-fallback">
          <p className="text-sm mb-4 leading-relaxed" style={{ color: "rgba(255,255,255,0.65)" }}>
            Embedded checkout isn't configured in this environment yet, so we'll take you to
            Stripe's hosted checkout instead.
          </p>
          <button
            onClick={hostedCheckout}
            disabled={busy}
            className="w-full sm:w-auto px-8 py-4 rounded-xl font-bold text-lg transition-transform hover:scale-[1.01] disabled:opacity-60 shadow-md"
            style={{ background: GOLD, color: INK }}
            data-testid="start-hosted-checkout-btn"
          >
            {busy ? "Opening Stripe Checkout…" : "Continue to Stripe Checkout →"}
          </button>
          <p className="text-xs mt-3" style={{ color: "rgba(255,255,255,0.45)" }}>{cfg.fallbackCta.split("·")[1]?.trim() || ""}</p>
        </div>
      )}
    </div>
  );
}

export default function StartPage({ kind }) {
  const [params, setParams] = useSearchParams();
  const isSeason = kind === "season_pass";
  const plan = isSeason && params.get("plan") === "annual" ? "annual" : "monthly";
  const cfgKey = isSeason ? (plan === "annual" ? "season_annual" : "season_monthly") : "free_sprint";
  const cfg = CONFIGS[cfgKey];
  const [betaSignup, setBetaSignup] = useState(params.get("beta") === "1");
  const [sprintPlan, setSprintPlan] = useState("monthly");
  useEffect(() => { window.scrollTo(0, 0); }, [kind]);

  const setPlan = (p) => setParams(p === "annual" ? { plan: "annual" } : {}, { replace: true });

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: INK }}>
      <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full pointer-events-none" style={{ background: "rgba(255,255,255,0.04)" }} />
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full pointer-events-none" style={{ background: "rgba(245,203,92,0.05)" }} />

      <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-16 lg:px-24 pt-6 sm:pt-8 pb-8 sm:pb-10 min-h-screen flex flex-col">
        <Link to="/" className="inline-flex items-center gap-2 text-sm hover:opacity-80 w-fit" style={{ color: "rgba(255,255,255,0.8)" }} data-testid="start-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to paydayrelay.com
        </Link>

        <div className="lg:flex lg:gap-16 lg:items-center mt-8 sm:mt-10 lg:mt-10 lg:flex-1">
          {/* Left, headline + one paragraph, top-aligned flush with the checkout column */}
          <div className="lg:w-[52%] mb-10 lg:mb-0">
            {kind === "free_sprint" && (
              <p className="text-base sm:text-lg font-bold uppercase tracking-[0.2em] mb-9" style={{ color: GOLD }} data-testid="start-sprint-kicker">
                Free Sprint · 4 relays · 28 days
              </p>
            )}
            <h1 className="text-[46px] sm:text-[68px] lg:text-[80px] font-medium leading-[1.03] mb-10 sm:mb-12 lg:mb-8" style={{ fontFamily: "'Fraunces', Georgia, serif", color: "#FFFFFF" }} data-testid="start-headline">
              {cfg.headline}
            </h1>
            <p className="text-xl sm:text-[27px] leading-relaxed max-w-2xl" style={{ color: "rgba(255,255,255,0.85)" }} data-testid="start-copy">
              {cfg.copy}
            </p>
          </div>

          {/* Right, plan toggle + real checkout */}
          <div className="lg:w-[48%]">
            {isSeason && (
              <div className="flex justify-center gap-2 mb-4" data-testid="season-plan-toggle">
                <button
                  onClick={() => setPlan("monthly")}
                  className="px-5 py-2.5 rounded-full font-bold text-sm transition-transform hover:scale-105"
                  style={plan === "monthly" ? { background: GOLD, color: INK } : { background: "rgba(255,255,255,0.1)", color: "#fff" }}
                  data-testid="season-plan-monthly"
                >
                  Monthly · $19/mo
                </button>
                <button
                  onClick={() => setPlan("annual")}
                  className="px-5 py-2.5 rounded-full font-bold text-sm transition-transform hover:scale-105"
                  style={plan === "annual" ? { background: GOLD, color: INK } : { background: "rgba(255,255,255,0.1)", color: "#fff" }}
                  data-testid="season-plan-annual"
                >
                  Annual · $119/yr
                </button>
              </div>
            )}
            {kind === "free_sprint" && !betaSignup && (
              <div className="mb-4" data-testid="sprint-plan-toggle">
                <p className="text-xs text-center uppercase tracking-[0.15em] mb-2 font-bold" style={{ color: "rgba(255,255,255,0.6)" }}>
                  After your free Sprint, continue on
                </p>
                <div className="flex justify-center gap-2">
                  <button
                    onClick={() => setSprintPlan("monthly")}
                    className="px-5 py-2.5 rounded-full font-bold text-sm transition-transform hover:scale-105"
                    style={sprintPlan === "monthly" ? { background: GOLD, color: INK } : { background: "rgba(255,255,255,0.1)", color: "#fff" }}
                    data-testid="sprint-plan-monthly"
                  >
                    Monthly · $19/mo
                  </button>
                  <button
                    onClick={() => setSprintPlan("annual")}
                    className="px-5 py-2.5 rounded-full font-bold text-sm transition-transform hover:scale-105"
                    style={sprintPlan === "annual" ? { background: GOLD, color: INK } : { background: "rgba(255,255,255,0.1)", color: "#fff" }}
                    data-testid="sprint-plan-annual"
                  >
                    Annual · $119/yr
                  </button>
                </div>
              </div>
            )}
            <EmbeddedCheckout cfg={cfg} key={`${cfg.productId}-${sprintPlan}`} betaSignup={betaSignup} sprintPlan={sprintPlan} />

            {kind === "free_sprint" && (
              <p className="text-sm text-center mt-3 leading-relaxed" style={{ color: "rgba(255,255,255,0.75)" }} data-testid="start-posttrial-price">
                {betaSignup
                  ? "You pay $0 today and no card is needed — your 28-day Sprint is completely free. When it ends, nothing happens automatically. Continue only if you want to, and as a founding beta member you keep 50% off for life — $9.50/mo instead of $19."
                  : (sprintPlan === "annual"
                      ? "You pay $0 today. Your 28-day Sprint is free — add your card now and it stays on file so your Season Pass begins automatically at day 28 at $119/yr (save 48% vs monthly). Cancel anytime before then and you're never charged."
                      : "You pay $0 today. Your 28-day Sprint is free — add your card now and it stays on file so your Season Pass begins automatically at day 28 at $19/mo. Cancel anytime before then and you're never charged. You can switch to annual later anytime.")}
              </p>
            )}

            {isSeason && betaSignup && (
              <p className="text-sm text-center mt-3 font-bold leading-relaxed" style={{ color: GOLD }} data-testid="start-season-beta-price">
                {plan === "annual"
                  ? "Beta price: 50% off for life — $59.50/yr instead of $119/yr, for as long as you stay."
                  : "Beta price: 50% off for life — $9.50/mo instead of $19/mo, for as long as you stay."}
              </p>
            )}

            {cfg.note && !(isSeason && betaSignup) && (
              <p className="text-sm text-center mt-4 font-bold" style={{ color: GOLD }} data-testid="start-price-note">
                {cfg.note}
              </p>
            )}
          </div>
        </div>

        <p className="text-xs text-center leading-relaxed mt-10 lg:mt-12" style={{ color: "rgba(255,255,255,0.5)" }} data-testid="start-disclaimer">
          Payday Relay is an educational planning system, not financial, tax, legal, or investment advice.{" "}
          By starting, you agree to our{" "}
          <Link to="/terms" className="underline hover:text-white transition-colors" data-testid="start-terms-link">
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link to="/privacy" className="underline hover:text-white transition-colors" data-testid="start-privacy-link">
            Privacy Policy
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
